import React, { useCallback, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import Button from '@atlaskit/button/default/button';
import AiAgentIcon from '@atlaskit/icon/core/ai-agent';

import { ANALYTICS_SOURCE, SHARE_CONTEXT_ACTION_SUBJECT_ID } from '../../analytics.ts';
import { ShareContextModalLoadable } from '../share-context-modal/ShareContextModalLoadable.tsx';
import { useTwgAnalyticsEvents } from '../../useTwgAnalyticsEvents.ts';

const i18n = defineMessages({
	shareWithAgent: {
		id: 'byline-connections.preview-panel.header.share-with-agent',
		defaultMessage: 'Share with agent',
		description:
			"Button label in the Teamwork Graph preview panel header and modal top bar. Opens options to share this content's context with an artificial intelligence agent.",
	},
});

export type TeamworkGraphShareContextButtonProps = {
	contentId?: string;
	testId?: string;
	/**
	 * Hands modal ownership to the caller: the click still reports analytics, but this component
	 * renders no modal and calls `onOpen` instead of opening one itself. Required inside the byline
	 * popup, where a click in the modal's portal reads as an outside click and unmounts the popup —
	 * taking the button and any modal it owned with it. Omit it to keep the self-contained
	 * behaviour the panel header and the Teamwork Graph modal rely on.
	 */
	onOpen?: () => void;
	/** Overrides the event source so a caller's surface is distinguishable in analytics. */
	source?: string;
	/** Forwarded to the modal's Rovo button. Unused with `onOpen`, where the caller owns the modal. */
	onAskRovo?: (prompt: string) => void;
};

export function TeamworkGraphShareContextButton({
	contentId,
	testId = 'teamwork-graph-modal-share-context',
	onOpen,
	source = ANALYTICS_SOURCE,
	onAskRovo,
}: TeamworkGraphShareContextButtonProps): React.JSX.Element {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useTwgAnalyticsEvents();
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [hasOpenedModal, setHasOpenedModal] = useState(false);

	const handleClick = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: SHARE_CONTEXT_ACTION_SUBJECT_ID,
				source,
			},
		}).fire();
		if (onOpen) {
			onOpen();
			return;
		}
		setHasOpenedModal(true);
		setIsModalOpen(true);
	}, [createAnalyticsEvent, onOpen, source]);

	const closeModal = useCallback(() => setIsModalOpen(false), []);

	return (
		<>
			<Button appearance="default" iconBefore={AiAgentIcon} onClick={handleClick} testId={testId}>
				{formatMessage(i18n.shareWithAgent)}
			</Button>
			{/* Keep the modal mounted so its close transition can finish. */}
			{hasOpenedModal && (
				<ShareContextModalLoadable
					isOpen={isModalOpen}
					onClose={closeModal}
					contentId={contentId}
					onAskRovo={onAskRovo}
				/>
			)}
		</>
	);
}
